import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  useTheme,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AccountsandFinanceurl, AssetFinanceUrl } from "../../../config";
import DynamicTableForJson from "../../../data/DynamicTable/dynamicTableForJson";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../../theme";

const base_url = AssetFinanceUrl.uri;

const STAGES = ["Field Officer", "Branch", "Region", "Company"];

// Single source of truth for the 4 approval-stage variants. Each stage only
// differs by which of these flags is set — collapsing what LoanManagement
// implements as 4 near-duplicate files into one parameterized component.
const LEVEL_CONFIG = {
  fieldOfficer: {
    label: "Field Officer",
    stepIndex: 0,
    parameters: { isCompany: 0, isRegion: 0, isBranch: 0, isFieldOfficer: 1 },
  },
  branch: {
    label: "Branch",
    stepIndex: 1,
    parameters: { isCompany: 0, isRegion: 0, isBranch: 1, isFieldOfficer: 0 },
  },
  region: {
    label: "Region",
    stepIndex: 2,
    parameters: { isCompany: 0, isRegion: 1, isBranch: 0, isFieldOfficer: 0 },
  },
  company: {
    label: "Company",
    stepIndex: 3,
    parameters: { isCompany: 1, isRegion: 0, isBranch: 0, isFieldOfficer: 0 },
  },
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export const getForcedSaleValue = (row) => {
  if (row === null || row === undefined) return null;
  if (typeof row === "number" || (typeof row === "string" && !isNaN(row) && row !== "")) {
    return Number(row);
  }
  const val = row.CurrentFSV ?? row.ForcedSaleValue ?? row.IndicativeValue;
  return val !== null && val !== undefined && val !== "" && !isNaN(val) ? Number(val) : null;
};

// LTV is computed client-side (principal / Forced Sale Value) rather than
// trusted from a backend field, keeping figures consistent.
export const computeLtv = (row) => {
  if (!row) return null;
  const principal = row.AmountToDisburse ?? row.LoanAmount;
  const fsv = getForcedSaleValue(row);
  if (!principal || !fsv || fsv <= 0) return null;
  return (Number(principal) / fsv) * 100;
};

const columns = [
  { field: "Id", headerName: "ID", flex: 0.6 },
  { field: "CustomerId", headerName: "Customer ID", flex: 1 },
  { field: "CustomerPhone", headerName: "Customer Phone", flex: 1 },
  { field: "FirstName", headerName: "First Name", flex: 1 },
  { field: "OtherNames", headerName: "Other Names", flex: 1.2 },
  { field: "ApplicationDate", headerName: "Booked Date", flex: 1.2 },
  { field: "AmountToDisburse", headerName: "Amount to Disburse", flex: 1 },
  { field: "LoanAmount", headerName: "Loan Amount", flex: 1 },
  { field: "InstallmentAmount", headerName: "Installment Amount", flex: 1 },
  {
    field: "AssetTypeName",
    headerName: "Collateral",
    flex: 1.2,
    renderCell: (params) => {
      console.log("Raw Row Data:", params.row);
      const label =
        [params.row.AssetTypeName, params.row.SubTypeName]
          .filter(Boolean)
          .join(" - ") || (params.row.AssetId ? `#${params.row.AssetId}` : "—");
      return label;
    },
  },
  {
    field: "CurrentFSV",
    headerName: "Forced Sale Value (FSV)",
    flex: 1.2,
    renderCell: (params) => formatCurrency(getForcedSaleValue(params.row)),
  },
  {
    field: "Ltv",
    headerName: "LTV (%)",
    flex: 0.7,
    renderCell: (params) => {
      const ltv = computeLtv(params.row);
      return ltv === null ? "—" : `${ltv.toFixed(1)}%`;
    },
  },
  { field: "Name", headerName: "Branch", flex: 1 },
];

const AssetLoanApprovalTable = ({ level }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const config = LEVEL_CONFIG[level];

  const [accountBalance, setAccountBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(
    config.parameters.isCompany === 1,
  );

  useEffect(() => {
    if (config.parameters.isCompany !== 1) return;
    (async () => {
      try {
        const response = await fetchJsonDynamic(
          "orgAccountBalance",
          null,
          null,
          AccountsandFinanceurl.uri,
        );
        if (response?.results) {
          const parsed = JSON.parse(response.results);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setAccountBalance(parsed[0].OrgAccountBalance);
          }
        }
      } catch (err) {
        console.error("Failed to fetch org account balance:", err);
      } finally {
        setLoadingBalance(false);
      }
    })();
  }, [config.parameters.isCompany]);

  const formattedBalance = (value) => {
    if (value === null || value === undefined) return "—";
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const actions = {
    entity: localStorage.getItem("SelectedRight"),
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: false,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: false,
      permission: "CreatePermission",
    },
    AssetApproveReject: {
      key: "ASSETAPPROVEREJECT",
      button_name: "Approve/Reject loan",
      Show_Button: true,
      permission: "hasNoPermission",
      roleGroupId: [1, 2, 3, 4, 5],
    },
    dataObject: {
      objectName: "allAssetLoanApplications",
      inputObjectName: "input",
      parameters: config.parameters,
      ignoreFields: [
        "LoanDisbursed",
        "Installments",
        "InstallmentAmount",
        "IsLoanApproved",
        "IsBranchApproved",
        "AverageCashSales",
        "FoFirstName",
        "FoLastName",
        "Metadata",
      ],
    },
    showStatus: true,
  };

  return (
    <Box m="20px">
      <Stepper activeStep={config.stepIndex} sx={{ mb: 3 }}>
        {STAGES.map((stage) => (
          <Step key={stage}>
            <StepLabel>{stage}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {config.parameters.isCompany === 1 && (
        <Tooltip
          title="This is the organisation's portal account balance. Always verify that this balance is sufficient before disbursing loans — disbursing without adequate funds will result in failed or blank disbursements."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 320,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                backgroundColor:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
                color: "#fff",
                p: 1.5,
              },
            },
            arrow: {
              sx: {
                color:
                  theme.palette.mode === "dark" ? colors.primary[300] : "#333",
              },
            },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: colors.primary[400],
              minWidth: 280,
              cursor: "default",
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[300],
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Org Account Balance
                </Typography>
                <InfoOutlinedIcon
                  sx={{ fontSize: 13, color: colors.grey[300] }}
                />
              </Box>
              {loadingBalance ? (
                <CircularProgress size={20} sx={{ mt: 0.5 }} />
              ) : (
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: colors.greenAccent[400] }}
                >
                  KES {formattedBalance(accountBalance)}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.4,
                  color: colors.grey[400],
                  fontStyle: "italic",
                  lineHeight: 1.3,
                }}
              >
                Ensure balance is adequate before disbursing loans.
              </Typography>
            </Box>
          </Paper>
        </Tooltip>
      )}

      <DynamicTableForJson
        title={`${config.label} Loan Appraisal`}
        subtitle="Review and approve or reject asset-backed loan applications"
        base_url={base_url}
        columns={columns}
        actions={actions}
      />
    </Box>
  );
};

export default AssetLoanApprovalTable;
